import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Share2, Menu, Plus, X, Smartphone, Download, Check } from "lucide-react";

/**
 * detectPlatform — returns "ios" | "android" | "other" for platform-specific
 * "Add to Home Screen" instructions. iPadOS 13+ reports as MacIntel with touch
 * so we treat it as iOS.
 */
export function detectPlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * PhoneStorefrontIllustration — pure Tailwind/SVG artwork of a smartphone
 * showing the branded storefront. Used across the modal, banner, and kid-zone.
 */
function PhoneStorefrontIllustration({ className = "" }) {
  return (
    <svg
      viewBox="0 0 200 220"
      className={className}
      role="img"
      aria-label="MyTableTopApp storefront on a smartphone"
    >
      {/* phone body */}
      <rect x="30" y="10" width="140" height="200" rx="22" fill="#2C3E50" />
      <rect x="36" y="18" width="128" height="184" rx="16" fill="#FFFDF7" />
      {/* awning stripes */}
      <g>
        <rect x="46" y="34" width="22" height="18" fill="#FF85A1" />
        <rect x="68" y="34" width="22" height="18" fill="#20B2AA" />
        <rect x="90" y="34" width="22" height="18" fill="#FF85A1" />
        <rect x="112" y="34" width="22" height="18" fill="#20B2AA" />
        <rect x="134" y="34" width="20" height="18" fill="#FF85A1" />
      </g>
      {/* store card */}
      <rect x="46" y="58" width="108" height="122" rx="10" fill="#ffffff" stroke="#E2E8F0" strokeWidth="1.5" />
      {/* windows */}
      <rect x="54" y="66" width="30" height="24" rx="4" fill="#FF85A1" opacity="0.85" />
      <rect x="116" y="66" width="30" height="24" rx="4" fill="#20B2AA" opacity="0.85" />
      {/* door */}
      <rect x="82" y="112" width="36" height="60" rx="10" fill="#20B2AA" />
      <circle cx="110" cy="142" r="2.2" fill="#ffffff" />
      {/* wordmark */}
      <text
        x="100"
        y="103"
        textAnchor="middle"
        fontFamily="Fredoka, system-ui"
        fontWeight="700"
        fontSize="12"
        fill="#2C3E50"
      >
        MyTableTopApp
      </text>
    </svg>
  );
}

/**
 * PlatformInstructionsBlock — dynamic browser-instruction copy with an inline
 * icon. Handles the three platforms we care about (iOS Safari, Android
 * Chrome, and desktop as a fallback).
 */
function PlatformInstructionsBlock({ platform }) {
  if (platform === "ios") {
    return (
      <div className="rounded-2xl bg-brandTeal/10 border border-brandTeal/25 p-4 text-sm text-brandSlate" data-testid="a2hs-ios-instructions">
        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandTeal mb-1.5">iPhone / iPad (Safari)</div>
        <div className="flex flex-wrap items-center gap-1.5 leading-relaxed">
          <span>Tap</span>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-slate-200 text-brandTeal">
            <Share2 className="w-3.5 h-3.5" />
          </span>
          <span className="font-bold">Share</span>
          <span>→</span>
          <span className="font-bold">Add to Home Screen</span>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white border border-slate-200 text-brandPink">
            <Plus className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    );
  }
  if (platform === "android") {
    return (
      <div className="rounded-2xl bg-brandPink/10 border border-brandPink/25 p-4 text-sm text-brandSlate" data-testid="a2hs-android-instructions">
        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-brandPink mb-1.5">Android (Chrome)</div>
        <div className="flex flex-wrap items-center gap-1.5 leading-relaxed">
          <span>Tap</span>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-slate-200 text-brandPink">
            <Menu className="w-3.5 h-3.5" />
          </span>
          <span className="font-bold">Menu</span>
          <span>→</span>
          <span className="font-bold">Add to Home Screen</span>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700" data-testid="a2hs-desktop-instructions">
      <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Desktop</div>
      <p className="leading-relaxed">
        Open <span className="font-mono font-bold">mytabletopapp.co.uk</span> on your phone to add the app to your Home Screen.
      </p>
    </div>
  );
}

/**
 * AddToHomeScreenModal — the pretty full-modal version used post-registration
 * and from the parent dashboard banner CTA. Guards against reopening when the
 * app is already installed (isStandalone).
 */
export function AddToHomeScreenModal({ open, onOpenChange }) {
  const platform = useMemo(() => detectPlatform(), []);
  useEffect(() => {
    if (open && isStandalone()) onOpenChange(false);
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop"
        data-testid="a2hs-modal"
      >
        <div className="relative px-6 pt-7 pb-5 bg-gradient-to-br from-brandPink via-brandPink to-brandTeal text-white">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-6 -bottom-10 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] opacity-90">
            <Smartphone className="w-4 h-4" /> Install
          </div>
          <DialogHeader className="relative mt-2 text-left space-y-1">
            <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold text-white">
              Add MyTableTopApp to your Home Screen
            </DialogTitle>
            <DialogDescription className="text-white/90 text-xs sm:text-sm leading-relaxed">
              A Kids Marketplace &mdash; one tap from your Home Screen. No app store required.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 bg-white space-y-4">
          <div className="flex items-center justify-center">
            <PhoneStorefrontIllustration className="w-32 h-36" />
          </div>

          <PlatformInstructionsBlock platform={platform} />

          <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <Check className="w-3.5 h-3.5 text-emerald-500" />
            Works offline for browsing your Wallet &amp; listings.
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-secondary w-full text-sm"
            data-testid="a2hs-modal-dismiss"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * AddToHomeScreenBanner — dismissible slim banner rendered on the Parent
 * Dashboard for mobile web users. Persists dismissal to localStorage so it
 * doesn't nag on every visit. Auto-hides when the app is already installed
 * or when the user is on desktop.
 */
export function AddToHomeScreenBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("mttApp:a2hsDismissed") === "1"; }
    catch { return false; }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const platform = useMemo(() => detectPlatform(), []);

  if (dismissed || isStandalone() || platform === "other") return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("mttApp:a2hsDismissed", "1"); } catch { /* noop */ }
  };

  return (
    <>
      <div
        className="mt-6 rounded-2xl bg-gradient-to-br from-brandPink/12 via-white to-brandTeal/12 border border-brandTeal/25 px-4 py-3 flex items-center gap-3"
        data-testid="a2hs-banner"
        role="region"
        aria-label="Add app to Home Screen"
      >
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white shadow-sm border border-slate-100 flex-shrink-0">
          <PhoneStorefrontIllustration className="w-7 h-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-brandSlate leading-tight">
            Get the app on your Home Screen
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
            Faster than the browser &mdash; one tap to your wallet &amp; kid&rsquo;s shop.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-brandTeal hover:bg-brandTealDark text-white font-bold text-xs shadow-sm transition flex-shrink-0"
          data-testid="a2hs-banner-cta"
        >
          <Download className="w-3.5 h-3.5" /> Add app
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 text-slate-400 hover:text-slate-700"
          aria-label="Dismiss"
          data-testid="a2hs-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <AddToHomeScreenModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}

/**
 * AddToHomeScreenFooterLink — compact link rendered at the bottom of the
 * Kid Zone login page so a parent setting up a child's tablet can add the
 * shortcut in one tap.
 */
export function AddToHomeScreenFooterLink() {
  const [open, setOpen] = useState(false);
  if (isStandalone()) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-xs font-bold text-brandTeal hover:text-brandTealDark underline underline-offset-4 decoration-brandTeal/40 hover:decoration-brandTeal transition"
        data-testid="a2hs-footer-link"
      >
        <Download className="w-3.5 h-3.5" /> Add App Shortcut to Home Screen
      </button>
      <AddToHomeScreenModal open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * usePostRegisterA2HSPrompt — fires the modal once, exactly once, per browser
 * after a fresh registration on mobile. Consumers set the flag by calling
 * `markJustRegistered()` from the Auth submit handler.
 *
 * StrictMode-safe: React.StrictMode mounts effects twice in dev; the cleanup
 * of the first invocation would clear the scheduled setOpen timer. We keep
 * an "armed" module flag that survives until setOpen actually fires, and we
 * (re-)schedule a fresh timer on every invocation while armed.
 */
let _postRegisterArmed = false;
let _postRegisterArmChecked = false;
export function usePostRegisterA2HSPrompt() {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // First-run check: consume the sessionStorage flag exactly once per page
    // load, but arm the state so subsequent StrictMode remounts can still
    // schedule the timer.
    if (!_postRegisterArmChecked) {
      _postRegisterArmChecked = true;
      const flag = sessionStorage.getItem("mttApp:justRegistered");
      if (flag) {
        sessionStorage.removeItem("mttApp:justRegistered");
        if (!isStandalone() && detectPlatform() !== "other") {
          _postRegisterArmed = true;
        }
      }
    }
    if (!_postRegisterArmed) return;
    timerRef.current = setTimeout(() => {
      _postRegisterArmed = false;
      setOpen(true);
    }, 900);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return { open, setOpen };
}

export function markJustRegistered() {
  try { sessionStorage.setItem("mttApp:justRegistered", "1"); } catch { /* noop */ }
}
